import React, { useState } from 'react';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import styles from './SiteForm.module.scss';

export type SiteFormValues = {
    name: string;
    business_central_id: string | undefined;
    valid_from?: string;
    valid_to?: string | null;
};

type SiteFormProps = {
    onSubmit: (values: SiteFormValues) => Promise<void>;
    onCancel: () => void;
    isSubmitting: boolean;
    submitLabel?: string;
    initialValues?: {
        name: string;
        business_central_id?: string | null;
        valid_from?: string;
        valid_to?: string | null;
    };
    showValidity?: boolean;
};

const SiteForm = ({
    onSubmit,
    onCancel,
    isSubmitting,
    submitLabel = 'Create Site',
    initialValues,
    showValidity = false,
}: SiteFormProps) => {
    const [name, setName] = useState(initialValues?.name ?? '');
    const [projectCode, setProjectCode] = useState(initialValues?.business_central_id ?? '');
    const [validFrom, setValidFrom] = useState(initialValues?.valid_from?.split('T')[0] ?? '');
    const [stillActive, setStillActive] = useState(initialValues?.valid_to === null || initialValues?.valid_to === undefined);
    const [validTo, setValidTo] = useState(initialValues?.valid_to?.split('T')[0] ?? '');

    const canSubmit = name.trim().length > 0 && !isSubmitting;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await onSubmit({
            name: name.trim(),
            business_central_id: projectCode.trim() || undefined,
            ...(showValidity && validFrom
                ? {
                      valid_from: new Date(validFrom).toISOString(),
                      valid_to: stillActive ? null : validTo ? new Date(validTo).toISOString() : null,
                  }
                : {}),
        });
    };

    return (
        <form onSubmit={handleSubmit} className={styles.form} data-testid='site-form'>
            <div className={styles.field}>
                <label htmlFor='site-name' className={styles.label}>
                    Site Name
                </label>
                <input
                    id='site-name'
                    type='text'
                    className={styles.input}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder='Enter site name'
                    autoFocus
                />
            </div>
            <div className={styles.field}>
                <label htmlFor='project-code' className={styles.label}>
                    Project Code
                </label>
                <input
                    id='project-code'
                    type='text'
                    className={styles.input}
                    value={projectCode}
                    onChange={e => setProjectCode(e.target.value)}
                    placeholder='Optional (e.g. PROJ04622)'
                />
            </div>
            {showValidity && (
                <>
                    <div className={styles.field}>
                        <label htmlFor='valid-from' className={styles.label}>
                            Valid from
                        </label>
                        <input
                            id='valid-from'
                            type='date'
                            className={styles.input}
                            value={validFrom}
                            onChange={e => setValidFrom(e.target.value)}
                            required
                        />
                    </div>
                    <div className={styles.field}>
                        <label className='flex items-center gap-2 cursor-pointer'>
                            <input
                                type='checkbox'
                                checked={stillActive}
                                onChange={e => setStillActive(e.target.checked)}
                                data-testid='still-active-checkbox'
                                className='w-4 h-4 accent-primary'
                            />
                            <span className={styles.label}>Still active (no end date)</span>
                        </label>
                        {!stillActive && (
                            <input
                                id='valid-to'
                                type='date'
                                className={styles.input}
                                value={validTo}
                                onChange={e => setValidTo(e.target.value)}
                                required
                                style={{ marginTop: 4 }}
                            />
                        )}
                    </div>
                </>
            )}
            <div className={styles.actions}>
                <PrimaryButton type='submit' disabled={!canSubmit} data-testid='site-form-submit'>
                    {isSubmitting ? 'Saving...' : submitLabel}
                </PrimaryButton>
                <SecondaryButton type='button' onClick={onCancel} data-testid='site-form-cancel'>
                    Cancel
                </SecondaryButton>
            </div>
        </form>
    );
};

export default SiteForm;
